import React, { useState } from 'react';
import { PlusIcon, ExternalLinkIcon } from 'lucide-react';
import { WindowRow, getCasementSurcharge, hasCasement, hasHingeInNotes, TEMPERED_GLASS_COST } from './WindowRow';
import { DoorRow, type DoorData } from './DoorRow';
import { SwingDoorRow, type SwingDoorData, isSwingDoorOperationComplete } from './SwingDoorRow';
import { PricingSummary } from './PricingSummary';
import { ExportActions } from './ExportActions';
import { createDefaultAdderSelection, calculateAdderTotal, DOOR_ADDERS } from './AdderSelector';
import { calculateBasePrice, SYNCHRONY_OPTIONS, type FinancingCompany, type SynchronyOption } from './pricingUtils';
import type { WindowData } from './WindowRow';
import type { ComparisonData } from '../App';
interface WindowCalculatorProps {
  onShowComparison: (data: ComparisonData) => void;
}
interface ProductLine {
  name: string;
  redline: number;
}
const PRODUCT_LINES: ProductLine[] = [{
  name: '80 Series',
  redline: 1700
}, {
  name: 'Mezzo',
  redline: 1750
}, {
  name: 'International',
  redline: 1900
}, {
  name: 'Western',
  redline: 2500
}, {
  name: 'Impervia',
  redline: 2450
}, {
  name: 'Anlin - Catalina',
  redline: 2300
}, {
  name: 'Anlin - Panoramic',
  redline: 2300
}];
const FRAME_COLORS: Record<string, string[]> = {
  '80 Series': ['White', 'Almond', 'Clay'],
  Mezzo: ['White', 'Almond', 'Clay'],
  International: ['Speckled White', 'Tan', 'Silver', 'Anodized Bronze'],
  Western: ['Standard'],
  Impervia: ['White', 'Black'],
  'Anlin - Catalina': ['White', 'Designer Black', 'Adobe', 'Tan'],
  'Anlin - Panoramic': ['White', 'Designer Black', 'Adobe', 'Tan']
};
const EXTERIOR_COLORS: Record<string, string[]> = {
  '80 Series': ['Black Paint', 'Black Laminate', 'Architectural Bronze', 'Silver'],
  Mezzo: ['Black Paint', 'Architectural Bronze', 'Silver'],
  International: [],
  Western: [],
  Impervia: [],
  'Anlin - Catalina': ['Architectural Black', 'Architectural Bronze', 'Architectural Silver', 'Classic Gray', 'Espresso', 'Taupe', 'Red Pepper', 'Rose Wood', 'Desert Tan', 'Sandstone', 'Divine Wine', 'Forest Green'],
  'Anlin - Panoramic': ['Architectural Black', 'Architectural Bronze', 'Architectural Silver', 'Classic Gray', 'Espresso', 'Taupe', 'Red Pepper', 'Rose Wood', 'Desert Tan', 'Sandstone', 'Divine Wine', 'Forest Green']
};
function generateId(): string {
  return Math.random().toString(36).substring(2, 10);
}
function createEmptyWindow(): WindowData {
  return {
    id: generateId(),
    qty: 1,
    width: 0,
    height: 0,
    glass: 'No',
    tempered: false,
    operations: [],
    adders: createDefaultAdderSelection(),
    included: true,
    notes: ''
  };
}
function createEmptyDoor(): DoorData {
  return {
    id: generateId(),
    qty: 1,
    width: 0,
    height: 0,
    glass: 'No',
    tempered: false,
    operations: [],
    adders: createDefaultAdderSelection(DOOR_ADDERS),
    included: true,
    notes: ''
  };
}
function createEmptySwingDoor(): SwingDoorData {
  return {
    id: generateId(),
    qty: 1,
    width: 0,
    height: 0,
    glass: 'No',
    tempered: false,
    panels: '',
    swingDirection: '',
    hinge: '',
    adders: createDefaultAdderSelection(DOOR_ADDERS),
    included: true,
    notes: ''
  };
}
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}
function formatCurrency2(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}
export function WindowCalculator({
  onShowComparison
}: WindowCalculatorProps) {
  const [selectedProductLine, setSelectedProductLine] = useState<string>(PRODUCT_LINES[0].name);
  const [selectedFrameColor, setSelectedFrameColor] = useState<string>(FRAME_COLORS[PRODUCT_LINES[0].name][0]);
  const [selectedExteriorColor, setSelectedExteriorColor] = useState<string>('None');
  const [financingCompany, setFinancingCompany] = useState<FinancingCompany>('None');
  const [selectedSynchronyOption, setSelectedSynchronyOption] = useState<SynchronyOption | null>(null);
  const [windows, setWindows] = useState<WindowData[]>([createEmptyWindow()]);
  const [doors, setDoors] = useState<DoorData[]>([]);
  const [swingDoors, setSwingDoors] = useState<SwingDoorData[]>([]);
  const [windowDiscount, setWindowDiscount] = useState<number>(0);
  const [sgdDiscount, setSgdDiscount] = useState<number>(0);
  const [swingDoorDiscount, setSwingDoorDiscount] = useState<number>(0);
  const currentProduct = PRODUCT_LINES.find(p => p.name === selectedProductLine) ?? PRODUCT_LINES[0];
  function handleProductLineChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const newLine = e.target.value;
    setSelectedProductLine(newLine);
    setSelectedFrameColor(FRAME_COLORS[newLine]?.[0] || 'Standard');
    setSelectedExteriorColor('None');
  }
  function handleFinancingCompanyChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const company = e.target.value as FinancingCompany;
    setFinancingCompany(company);
    if (company !== 'Synchrony') {
      setSelectedSynchronyOption(null);
    }
  }
  function handleSynchronyOptionChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const option = SYNCHRONY_OPTIONS.find(opt => opt.name === e.target.value);
    setSelectedSynchronyOption(option || null);
  }
  function handleShowCustomerPricing() {
    const includedWindows = windows.filter(w => w.included);
    const includedDoors = doors.filter(d => d.included);
    const includedSwingDoors = swingDoors.filter(d => d.included);
    const allIncluded = [...includedWindows, ...includedDoors, ...includedSwingDoors];
    const totalSqFt = allIncluded.reduce((sum, item) => {
      const sqIn = item.width * item.height;
      return sum + sqIn / 144 * item.qty;
    }, 0);
    onShowComparison({
      productLine: currentProduct.name,
      selectedFrameColor,
      selectedExteriorColor,
      totalBasePrice,
      totalAdderCost,
      grandTotal,
      discountPercent: grandTotal > 0 ? totalDiscountAmount / grandTotal * 100 : 0,
      discountAmount: totalDiscountAmount,
      totalDue,
      totalWindowCount: totalItemCount,
      totalSqFt,
      financingCompany,
      synchronyOption: selectedSynchronyOption
    });
  }
  function handleAddWindow() {
    setWindows(prev => [...prev, createEmptyWindow()]);
  }
  function handleUpdateWindow(id: string, updates: Partial<WindowData>) {
    setWindows(prev => prev.map(w => w.id === id ? {
      ...w,
      ...updates
    } : w));
  }
  function handleRemoveWindow(id: string) {
    setWindows(prev => prev.filter(w => w.id !== id));
  }
  function handleAddDoor() {
    setDoors(prev => [...prev, createEmptyDoor()]);
  }
  function handleUpdateDoor(id: string, updates: Partial<DoorData>) {
    setDoors(prev => prev.map(d => d.id === id ? {
      ...d,
      ...updates
    } : d));
  }
  function handleRemoveDoor(id: string) {
    setDoors(prev => prev.filter(d => d.id !== id));
  }
  function handleAddSwingDoor() {
    setSwingDoors(prev => [...prev, createEmptySwingDoor()]);
  }
  function handleUpdateSwingDoor(id: string, updates: Partial<SwingDoorData>) {
    setSwingDoors(prev => prev.map(d => d.id === id ? {
      ...d,
      ...updates
    } : d));
  }
  function handleRemoveSwingDoor(id: string) {
    setSwingDoors(prev => prev.filter(d => d.id !== id));
  }
  const includedWindows = windows.filter(w => w.included);
  const includedDoors = doors.filter(d => d.included);
  const includedSwingDoors = swingDoors.filter(d => d.included);
  const allItems = [...includedWindows, ...includedDoors, ...includedSwingDoors];
  const totalItemCount = allItems.reduce((sum, item) => sum + item.qty, 0);
  const exteriorColorCost = selectedExteriorColor !== 'None' ? 450 : 0;
  const isAnlin = currentProduct.name === 'Anlin - Catalina' || currentProduct.name === 'Anlin - Panoramic';
  const frameColorUpcharge = isAnlin && selectedFrameColor === 'Designer Black' ? 1200 : 0;
  const perItemColorCost = exteriorColorCost + frameColorUpcharge;
  // --- Per-type calculations ---
  const windowCount = includedWindows.reduce((sum, w) => sum + w.qty, 0);
  const windowBasePrice = includedWindows.reduce((sum, w) => sum + calculateBasePrice(w.width, w.height, currentProduct.name) * w.qty, 0);
  const windowAdderCost = includedWindows.reduce((sum, w) => sum + (calculateAdderTotal(w.adders) + perItemColorCost + getCasementSurcharge(w.operations) + (w.tempered ? TEMPERED_GLASS_COST : 0)) * w.qty, 0);
  const windowSubtotal = windowBasePrice + windowAdderCost;
  const effWindowDiscount = Math.min(Math.max(windowDiscount, 0), 100);
  const windowDiscountAmt = windowSubtotal * (effWindowDiscount / 100);
  const windowDue = windowSubtotal - windowDiscountAmt;
  const sgdCount = includedDoors.reduce((sum, d) => sum + d.qty, 0);
  const sgdBasePrice = includedDoors.reduce((sum, d) => sum + calculateBasePrice(d.width, d.height, currentProduct.name) * d.qty, 0);
  const sgdAdderCost = includedDoors.reduce((sum, d) => sum + (calculateAdderTotal(d.adders) + perItemColorCost + (d.tempered ? TEMPERED_GLASS_COST : 0)) * d.qty, 0);
  const sgdSubtotal = sgdBasePrice + sgdAdderCost;
  const effSgdDiscount = Math.min(Math.max(sgdDiscount, 0), 100);
  const sgdDiscountAmt = sgdSubtotal * (effSgdDiscount / 100);
  const sgdDue = sgdSubtotal - sgdDiscountAmt;
  const swingCount = includedSwingDoors.reduce((sum, d) => sum + d.qty, 0);
  const swingBasePrice = includedSwingDoors.reduce((sum, d) => sum + calculateBasePrice(d.width, d.height, currentProduct.name) * 2 * d.qty, 0);
  const swingAdderCost = includedSwingDoors.reduce((sum, d) => sum + (calculateAdderTotal(d.adders) + perItemColorCost + (d.tempered ? TEMPERED_GLASS_COST : 0)) * d.qty, 0);
  const swingSubtotal = swingBasePrice + swingAdderCost;
  const effSwingDiscount = Math.min(Math.max(swingDoorDiscount, 0), 100);
  const swingDiscountAmt = swingSubtotal * (effSwingDiscount / 100);
  const swingDue = swingSubtotal - swingDiscountAmt;
  // --- Combined totals ---
  const totalBasePrice = windowBasePrice + sgdBasePrice + swingBasePrice;
  const totalAdderCost = windowAdderCost + sgdAdderCost + swingAdderCost;
  const grandTotal = totalBasePrice + totalAdderCost;
  const totalDiscountAmount = windowDiscountAmt + sgdDiscountAmt + swingDiscountAmt;
  const totalDue = windowDue + sgdDue + swingDue;
  const avgPerWindow = windowCount > 0 ? windowDue / windowCount : 0;
  // --- Window redline ---
  const baseRedline = (currentProduct.name === 'Anlin - Catalina' || currentProduct.name === 'Anlin - Panoramic') && selectedFrameColor === 'Designer Black' ? 3000 : currentProduct.redline;
  const dealerFee = selectedSynchronyOption?.dealerFeePercent || 0;
  const redlineWithDealerFee = dealerFee > 0 ? baseRedline / (1 - dealerFee) : baseRedline;
  // Frame color upcharge is already baked into the redline value (e.g. Designer Black = $3,000 floor),
  // so exclude it from the per-window adder portion to avoid double-counting.
  // Stucco Cut Back contributes only 50% to the redline (we typically discount it 50%).
  const stuccoRedlineDiscount = includedWindows.reduce((sum, w) => {
    const stucco = w.adders['Stucco Cut Back'];
    if (stucco?.checked) return sum + stucco.amount * 0.5 * w.qty;
    return sum;
  }, 0);
  const windowRedlineAdderCost = Math.max(0, windowAdderCost - frameColorUpcharge * windowCount - stuccoRedlineDiscount);
  const effectiveRedlinePerWindow = windowCount > 0 ? redlineWithDealerFee + windowRedlineAdderCost / windowCount : redlineWithDealerFee;
  // --- Redline warnings ---
  const windowWarnings: string[] = [];
  if (windowCount > 0 && avgPerWindow > 0 && avgPerWindow < effectiveRedlinePerWindow) {
    windowWarnings.push(`Avg $/window (${formatCurrency(Math.round(avgPerWindow))}) is below redline (${formatCurrency(Math.round(effectiveRedlinePerWindow))})`);
  }
  const sgdWarnings: string[] = [];
  if (sgdCount > 0) {
    includedDoors.forEach((door, idx) => {
      const sqIn = door.width * door.height;
      if (sqIn <= 0) return;
      const bp = calculateBasePrice(door.width, door.height, currentProduct.name);
      const ac = calculateAdderTotal(door.adders) + perItemColorCost;
      const unitPrice = (bp + ac) * (1 - effSgdDiscount / 100);
      const minPrice = sqIn < 6400 ? 3000 : 5000;
      const sizeLabel = sqIn < 6400 ? `under 80×80` : `80×80+`;
      if (unitPrice < minPrice) {
        sgdWarnings.push(`SGD #${idx + 1} (${door.width}"×${door.height}", ${sizeLabel}): ${formatCurrency2(unitPrice)} is below ${formatCurrency(minPrice)} minimum`);
      }
    });
  }
  const swingWarnings: string[] = [];
  if (swingCount > 0) {
    includedSwingDoors.forEach((door, idx) => {
      if (door.width <= 0 || door.height <= 0) return;
      const bp = calculateBasePrice(door.width, door.height, currentProduct.name) * 2;
      // Price excluding dog door adders
      const nonDogAdderCost = Object.entries(door.adders).filter(([name, v]) => v.checked && !name.includes('Dog Door')).reduce((sum, [, v]) => sum + v.amount, 0);
      const unitPriceNoDog = (bp + nonDogAdderCost + perItemColorCost) * (1 - effSwingDiscount / 100);
      if (unitPriceNoDog < 4000) {
        swingWarnings.push(`Swing Door #${idx + 1} (${door.width}"×${door.height}"): ${formatCurrency2(unitPriceNoDog)} (excl. dog door) is below $4,000 minimum`);
      }
    });
  }
  const isAtRedlineLimit = false;
  return <div className="min-h-screen w-full bg-gray-100" data-id="element-155">
      {/* Header */}
      <header className="bg-[#2E5E3F] shadow-sm" data-id="element-156">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between" data-id="element-157">
          <div data-id="element-158">
            <h1 className="text-xl font-bold text-white tracking-tight" data-id="element-159">
              Window Pricing Calculator
            </h1>
            <p className="text-emerald-200 text-sm mt-0.5" data-id="element-160">
              Sales quoting tool
            </p>
          </div>
          <div className="text-right text-sm text-emerald-100" data-id="element-161">
            {totalItemCount} item{totalItemCount !== 1 ? 's' : ''} &middot;{' '}
            <span className="font-semibold text-white" data-id="element-162">
              {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 2
            }).format(totalDue)}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6" data-id="element-163">
        {/* Product Line, Color & Financing Selector */}
        <div className="bg-white border border-gray-200 rounded-lg px-6 py-4 flex flex-wrap items-center gap-6" data-id="element-164">
          <div className="flex items-center gap-4" data-id="element-165">
            <label htmlFor="product-line" className="text-sm font-medium text-gray-700" data-id="element-166">
              Product Line
            </label>
            <select id="product-line" value={selectedProductLine} onChange={handleProductLineChange} className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 min-w-[180px]" data-id="element-167">
              {PRODUCT_LINES.map(pl => <option key={pl.name} value={pl.name} data-id="element-168">
                  {pl.name}
                </option>)}
            </select>
          </div>

          <div className="flex items-center gap-4" data-id="element-169">
            <label htmlFor="frame-color" className="text-sm font-medium text-gray-700" data-id="element-170">
              Frame Color
            </label>
            <select id="frame-color" value={selectedFrameColor} onChange={e => setSelectedFrameColor(e.target.value)} className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 min-w-[180px]" data-id="element-171">
              {FRAME_COLORS[selectedProductLine]?.map(color => <option key={color} value={color} data-id="element-172">
                  {color}
                </option>)}
            </select>
          </div>

          {EXTERIOR_COLORS[selectedProductLine]?.length > 0 && <div className="flex items-center gap-4" data-id="element-173">
              <label htmlFor="exterior-color" className="text-sm font-medium text-gray-700" data-id="element-174">
                Exterior Color
              </label>
              <select id="exterior-color" value={selectedExteriorColor} onChange={e => setSelectedExteriorColor(e.target.value)} className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 min-w-[180px]" data-id="element-175">
                <option value="None" data-id="element-176">None</option>
                {EXTERIOR_COLORS[selectedProductLine].map(color => <option key={color} value={color} data-id="element-177">
                    {color} (+$450)
                  </option>)}
              </select>
            </div>}

          <div className="flex items-center gap-4" data-id="element-178">
            <label htmlFor="financing-company" className="text-sm font-medium text-gray-700" data-id="element-179">
              Financing
            </label>
            <select id="financing-company" value={financingCompany} onChange={handleFinancingCompanyChange} className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 min-w-[180px]" data-id="element-180">
              <option value="None" data-id="element-181">None</option>
              <option value="Synchrony" data-id="element-182">Synchrony</option>
              <option value="Hearth" data-id="element-183">Hearth</option>
            </select>
          </div>

          {financingCompany === 'Synchrony' && <div className="flex items-center gap-4" data-id="element-184">
              <label htmlFor="synchrony-option" className="text-sm font-medium text-gray-700" data-id="element-185">
                Plan
              </label>
              <select id="synchrony-option" value={selectedSynchronyOption?.name || ''} onChange={handleSynchronyOptionChange} className="px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 min-w-[220px]" data-id="element-186">
                <option value="" data-id="element-187">Select financing plan</option>
                {SYNCHRONY_OPTIONS.map(opt => <option key={opt.plan} value={opt.name} data-id="element-188">
                    {opt.name}
                  </option>)}
              </select>
            </div>}
        </div>

        {/* Windows Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-189">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50" data-id="element-190">
            <h2 className="text-sm font-semibold text-gray-800" data-id="element-191">Windows</h2>
          </div>
          <div className="overflow-x-auto" data-id="element-192">
            <table className="w-full" data-id="element-193">
              <thead data-id="element-194">
                <tr className="bg-gray-50 border-b border-gray-200" data-id="element-195">
                  <th className="px-2 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center w-10" data-id="element-196">
                    <span className="sr-only" data-id="element-197">Include</span>✓
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center w-16" data-id="element-198">
                    #
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-199">
                    Qty
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-200">
                    Width (in)
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-201">
                    Height (in)
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right" data-id="element-202">
                    Sq Ft
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-203">
                    Operation
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-204">
                    Obscured
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center" data-id="element-205">
                    Tempered
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-206">
                    Adders
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-207">
                    Notes / Room
                  </th>
                  <th className="px-3 py-3 w-12" data-id="element-208"></th>
                </tr>
              </thead>
              <tbody data-id="element-209">
                {windows.map((win, idx) => <WindowRow key={win.id} window={win} index={idx} productLine={currentProduct.name} exteriorColorCost={perItemColorCost} onUpdate={handleUpdateWindow} onRemove={handleRemoveWindow} canRemove={windows.length > 1 || doors.length > 0} data-id="element-210" />)}
              </tbody>
            </table>
          </div>

          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50/50" data-id="element-211">
            <button type="button" onClick={handleAddWindow} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-[#2E5E3F] bg-emerald-50 border border-emerald-200 rounded-md hover:bg-emerald-100 transition-colors" data-id="element-212">
              <PlusIcon className="w-4 h-4" data-id="element-213" />
              Add Window
            </button>
          </div>
        </div>

        {/* Sliding Glass Doors Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-214">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50" data-id="element-215">
            <h2 className="text-sm font-semibold text-gray-800" data-id="element-216">
              Sliding Glass Doors
            </h2>
          </div>
          <div className="overflow-x-auto" data-id="element-217">
            <table className="w-full" data-id="element-218">
              <thead data-id="element-219">
                <tr className="bg-gray-50 border-b border-gray-200" data-id="element-220">
                  <th className="px-2 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center w-10" data-id="element-221">
                    <span className="sr-only" data-id="element-222">Include</span>✓
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center w-16" data-id="element-223">
                    #
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-224">
                    Qty
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-225">
                    Width (in)
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-226">
                    Height (in)
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right" data-id="element-227">
                    Sq Ft
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-228">
                    Operation
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-229">
                    Obscured
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center" data-id="element-230">
                    Tempered
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-231">
                    Adders
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-232">
                    Notes / Room
                  </th>
                  <th className="px-3 py-3 w-12" data-id="element-233"></th>
                </tr>
              </thead>
              <tbody data-id="element-234">
                {doors.length === 0 ? <tr data-id="element-235">
                    <td colSpan={11} className="px-4 py-8 text-center text-sm text-gray-500" data-id="element-236">
                      No sliding glass doors added yet.
                    </td>
                  </tr> : doors.map((door, idx) => <DoorRow key={door.id} door={door} index={idx} productLine={currentProduct.name} exteriorColorCost={perItemColorCost} onUpdate={handleUpdateDoor} onRemove={handleRemoveDoor} canRemove={true} data-id="element-237" />)}
              </tbody>
            </table>
          </div>

          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50/50" data-id="element-238">
            <button type="button" onClick={handleAddDoor} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-[#2E5E3F] bg-emerald-50 border border-emerald-200 rounded-md hover:bg-emerald-100 transition-colors" data-id="element-239">
              <PlusIcon className="w-4 h-4" data-id="element-240" />
              Add Sliding Door
            </button>
          </div>
        </div>

        {/* Swing Doors Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-241">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50" data-id="element-242">
            <h2 className="text-sm font-semibold text-gray-800" data-id="element-243">Swing Doors</h2>
          </div>
          <div className="overflow-x-auto" data-id="element-244">
            <table className="w-full" data-id="element-245">
              <thead data-id="element-246">
                <tr className="bg-gray-50 border-b border-gray-200" data-id="element-247">
                  <th className="px-2 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center w-10" data-id="element-248">
                    <span className="sr-only" data-id="element-249">Include</span>✓
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center w-16" data-id="element-250">
                    #
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-251">
                    Qty
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-252">
                    Width (in)
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-253">
                    Height (in)
                  </th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right" data-id="element-254">
                    Sq Ft
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-255">
                    Operation
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-256">
                    Obscured
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-center" data-id="element-257">
                    Tempered
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-258">
                    Adders
                  </th>
                  <th className="px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-left" data-id="element-259">
                    Notes / Room
                  </th>
                  <th className="px-3 py-3 w-12" data-id="element-260"></th>
                </tr>
              </thead>
              <tbody data-id="element-261">
                {swingDoors.length === 0 ? <tr data-id="element-262">
                    <td colSpan={11} className="px-4 py-8 text-center text-sm text-gray-500" data-id="element-263">
                      No swing doors added yet.
                    </td>
                  </tr> : swingDoors.map((door, idx) => <SwingDoorRow key={door.id} door={door} index={idx} productLine={currentProduct.name} exteriorColorCost={perItemColorCost} onUpdate={handleUpdateSwingDoor} onRemove={handleRemoveSwingDoor} canRemove={true} data-id="element-264" />)}
              </tbody>
            </table>
          </div>

          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50/50" data-id="element-265">
            <button type="button" onClick={handleAddSwingDoor} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-[#2E5E3F] bg-emerald-50 border border-emerald-200 rounded-md hover:bg-emerald-100 transition-colors" data-id="element-266">
              <PlusIcon className="w-4 h-4" data-id="element-267" />
              Add Swing Door
            </button>
          </div>
        </div>

        {/* Summary with per-type discounts */}
        <PricingSummary productLine={currentProduct.name} totalItemCount={totalItemCount} totalBasePrice={totalBasePrice} totalAdderCost={totalAdderCost} grandTotal={grandTotal} totalDue={totalDue} types={[{
        label: 'Windows',
        count: windowCount,
        subtotal: windowSubtotal,
        discountPercent: effWindowDiscount,
        onDiscountChange: setWindowDiscount,
        discountAmount: windowDiscountAmt,
        totalDue: windowDue,
        warnings: windowWarnings
      }, {
        label: 'Sliding Glass Doors',
        count: sgdCount,
        subtotal: sgdSubtotal,
        discountPercent: effSgdDiscount,
        onDiscountChange: setSgdDiscount,
        discountAmount: sgdDiscountAmt,
        totalDue: sgdDue,
        warnings: sgdWarnings
      }, {
        label: 'Swing Doors',
        count: swingCount,
        subtotal: swingSubtotal,
        discountPercent: effSwingDiscount,
        onDiscountChange: setSwingDoorDiscount,
        discountAmount: swingDiscountAmt,
        totalDue: swingDue,
        warnings: swingWarnings
      }]} data-id="element-268" />

        {/* Financing Summary — visible when Synchrony plan is selected */}
        {financingCompany === 'Synchrony' && selectedSynchronyOption && <div className="bg-white border border-gray-200 rounded-lg px-6 py-4" data-id="element-269">
            <h3 className="text-sm font-semibold text-gray-800 mb-3" data-id="element-270">
              Financing Summary — {selectedSynchronyOption.name}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-id="element-271">
              <div data-id="element-272">
                <p className="text-xs text-gray-500 uppercase tracking-wide" data-id="element-273">
                  Finance Rate
                </p>
                <p className="text-lg font-semibold text-gray-900 tabular-nums" data-id="element-274">
                  {selectedSynchronyOption.interestRate}%
                </p>
              </div>
              <div data-id="element-275">
                <p className="text-xs text-gray-500 uppercase tracking-wide" data-id="element-276">
                  Term
                </p>
                <p className="text-lg font-semibold text-gray-900 tabular-nums" data-id="element-277">
                  {selectedSynchronyOption.termMonths} months
                </p>
              </div>
              <div data-id="element-278">
                <p className="text-xs text-gray-500 uppercase tracking-wide" data-id="element-279">
                  Monthly Payment
                </p>
                <p className="text-lg font-semibold text-emerald-700 tabular-nums" data-id="element-280">
                  {formatCurrency2(totalDue * selectedSynchronyOption.monthlyFactor)}
                  /mo
                </p>
              </div>
              <div data-id="element-281">
                <p className="text-xs text-gray-500 uppercase tracking-wide" data-id="element-282">
                  Financing Fee
                </p>
                <p className="text-lg font-semibold text-gray-900 tabular-nums" data-id="element-283">
                  {(selectedSynchronyOption.dealerFeePercent * 100).toFixed(2)}%
                </p>
              </div>
            </div>
          </div>}

        {/* Export Actions */}
        <div className="bg-white border border-gray-200 rounded-lg px-6 py-4" data-id="element-284">
          <div className="flex items-center justify-between flex-wrap gap-4" data-id="element-285">
            <div data-id="element-286">
              <h3 className="text-sm font-semibold text-gray-800" data-id="element-287">
                Export Quote
              </h3>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-288">
                Copy to paste into your Go High Level contract, or download as
                CSV
              </p>
            </div>
            <div className="flex items-center gap-3" data-id="element-289">
              <ExportActions windowItems={includedWindows} doorItems={includedDoors} swingDoorItems={includedSwingDoors} productLine={currentProduct.name} selectedFrameColor={selectedFrameColor} selectedExteriorColor={selectedExteriorColor} exteriorColorCost={perItemColorCost} totalBasePrice={totalBasePrice} totalAdderCost={totalAdderCost} grandTotal={grandTotal} windowDiscount={effWindowDiscount} sgdDiscount={effSgdDiscount} swingDoorDiscount={effSwingDiscount} totalDiscountAmount={totalDiscountAmount} totalDue={totalDue} totalWindowCount={totalItemCount} avgPerWindow={avgPerWindow} data-id="element-290" />
              <button type="button" onClick={handleShowCustomerPricing} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors" data-id="element-291">
                <ExternalLinkIcon className="w-4 h-4" data-id="element-292" />
                Show Customer Pricing
              </button>
            </div>
          </div>
        </div>

        {/* Redline — below export, away from customer view */}
        <div className="bg-gray-50/60 border border-gray-100 rounded-lg px-6 py-4 space-y-3" data-id="element-293">
          <div className="flex items-center justify-between flex-wrap gap-4" data-id="element-294">
            <div data-id="element-295">
              <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide" data-id="element-296">
                Rep Only — Redline (Windows)
              </h3>
              <p className="text-[11px] text-gray-300 mt-0.5" data-id="element-297">
                Minimum avg price per window for {currentProduct.name} (
                {selectedFrameColor}
                {selectedExteriorColor !== 'None' ? ` / ${selectedExteriorColor}` : ''}
                )
                {dealerFee > 0 && ` — includes ${(dealerFee * 100).toFixed(2)}% financing fee`}
              </p>
            </div>
            <div className="text-right" data-id="element-298">
              <p className="text-sm font-medium text-gray-400 tabular-nums" data-id="element-299">
                {formatCurrency(effectiveRedlinePerWindow)}/window
              </p>
              <p className="text-[11px] text-gray-300 mt-1 tabular-nums" data-id="element-300">
                Avg $/Window: {formatCurrency2(avgPerWindow)}
                {windowCount > 0 && ` (${windowCount} window${windowCount !== 1 ? 's' : ''})`}
              </p>
            </div>
          </div>
          {windowWarnings.length > 0 && <div className="border-t border-red-200/50 pt-2" data-id="element-301">
              {windowWarnings.map((w, i) => <p key={i} className="text-xs font-medium text-red-400 flex items-center gap-1.5" data-id="element-302">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" data-id="element-303" />
                  {w}
                </p>)}
            </div>}
          {dealerFee > 0 && windowCount > 0 && <div className="border-t border-gray-200/50 pt-3 flex items-center justify-between flex-wrap gap-4" data-id="element-304">
              <div data-id="element-305">
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wide" data-id="element-306">
                  Financing Fee Impact
                </p>
                <p className="text-[11px] text-gray-300 mt-0.5" data-id="element-307">
                  Additional cost to customer from{' '}
                  {(dealerFee * 100).toFixed(2)}% financing fee
                </p>
              </div>
              <div className="text-right" data-id="element-308">
                <p className="text-sm font-medium text-amber-500 tabular-nums" data-id="element-309">
                  +
                  {formatCurrency2((redlineWithDealerFee - baseRedline) * windowCount)}{' '}
                  total
                </p>
                <p className="text-[11px] text-gray-300 mt-1 tabular-nums" data-id="element-310">
                  +{formatCurrency2(redlineWithDealerFee - baseRedline)}/window
                </p>
              </div>
            </div>}

          {/* SGD Redline */}
          {sgdCount > 0 && <div className="border-t border-gray-200/50 pt-3" data-id="element-311">
              <div className="flex items-center justify-between flex-wrap gap-4" data-id="element-312">
                <div data-id="element-313">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide" data-id="element-314">
                    SGD Minimums
                  </p>
                  <p className="text-[11px] text-gray-300 mt-0.5" data-id="element-315">
                    Under 80×80: $3,000/door · 80×80+: $5,000/door
                  </p>
                </div>
              </div>
              {sgdWarnings.length > 0 && <div className="mt-2 space-y-1" data-id="element-316">
                  {sgdWarnings.map((w, i) => <p key={i} className="text-xs font-medium text-red-400 flex items-center gap-1.5" data-id="element-317">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" data-id="element-318" />
                      {w}
                    </p>)}
                </div>}
            </div>}

          {/* Swing Door Redline */}
          {swingCount > 0 && <div className="border-t border-gray-200/50 pt-3" data-id="element-319">
              <div className="flex items-center justify-between flex-wrap gap-4" data-id="element-320">
                <div data-id="element-321">
                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide" data-id="element-322">
                    Swing Door Minimums
                  </p>
                  <p className="text-[11px] text-gray-300 mt-0.5" data-id="element-323">
                    $4,000/door minimum (excluding dog door adders)
                  </p>
                </div>
              </div>
              {swingWarnings.length > 0 && <div className="mt-2 space-y-1" data-id="element-324">
                  {swingWarnings.map((w, i) => <p key={i} className="text-xs font-medium text-red-400 flex items-center gap-1.5" data-id="element-325">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" data-id="element-326" />
                      {w}
                    </p>)}
                </div>}
            </div>}
        </div>
      </main>
    </div>;
}